import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import TicketList from './pages/TicketList';
import NewTicket from './pages/NewTicket';
import TicketDetail from './pages/TicketDetail';
import Users from './pages/Users';
import Reports from './pages/Reports';
import AuditLog from './pages/AuditLog';
import Profile from './pages/Profile';
import LoginHistory from './pages/LoginHistory';
import SADashboard from './pages/SADashboard';
import SANewRequest from './pages/SANewRequest';
import SARequestDetail from './pages/SARequestDetail';
import SAReports from './pages/SAReports';
import './index.css';

// Global fix: scroll focused input into view when mobile keyboard opens
function MobileKeyboardFix() {
  useEffect(() => {
    const handler = (e) => {
      const el = e.target;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 350); // delay allows keyboard to finish animating up
      }
    };
    document.addEventListener('focusin', handler);
    return () => document.removeEventListener('focusin', handler);
  }, []);
  return null;
}

const PrivateRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
};

function SAOrDashboard() {
  const { user } = useAuth();
  if (user?.role === 'sa_initiator' || user?.role === 'sa_approver') return <SADashboard />;
  return <Dashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MobileKeyboardFix />
        <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ForgotPassword />} />
          <Route path="/" element={<PrivateRoute><Layout><SAOrDashboard /></Layout></PrivateRoute>} />
          <Route path="/tickets" element={<PrivateRoute><Layout><TicketList /></Layout></PrivateRoute>} />
          <Route path="/tickets/new" element={<PrivateRoute><Layout><NewTicket /></Layout></PrivateRoute>} />
          <Route path="/tickets/:id" element={<PrivateRoute><Layout><TicketDetail /></Layout></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute roles={['super_admin']}><Layout><Users /></Layout></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute roles={['ict_manager','super_admin']}><Layout><Reports /></Layout></PrivateRoute>} />
          <Route path="/audit" element={<PrivateRoute roles={['ict_manager','super_admin']}><Layout><AuditLog /></Layout></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><Layout><Profile /></Layout></PrivateRoute>} />
          <Route path="/login-history" element={<PrivateRoute roles={['super_admin']}><Layout><LoginHistory /></Layout></PrivateRoute>} />
          <Route path="/sa" element={<PrivateRoute><Layout><SADashboard /></Layout></PrivateRoute>} />
          <Route path="/sa/new" element={<PrivateRoute roles={['sa_initiator']}><Layout><SANewRequest /></Layout></PrivateRoute>} />
          <Route path="/sa/reports" element={<PrivateRoute roles={['sa_initiator','sa_approver','ict_manager','super_admin','branch_manager']}><Layout><SAReports /></Layout></PrivateRoute>} />
          <Route path="/sa/:id" element={<PrivateRoute><Layout><SARequestDetail /></Layout></PrivateRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
